import type { ButtonProps } from '@cmg/design-system';
import { Grid, Section } from '@cmg/design-system';
import { Form, FormikProvider, useFormik } from 'formik';
import defaultTo from 'lodash/defaultTo';
import React, { type ReactElement } from 'react';

import { FormikUnsavedChangesGuard } from '../../../common/context/formik-unsaved-changes';
import { OfferingType, SecurityType } from '../../../graphql';
import type {
  OfferingSetup_BasicInfoRoute_OfferingFullPartsFragment,
  OfferingSetup_BasicInfoRoute_PublishedOfferingFullPartsFragment,
} from '../graphql';
import { FormType, getValidationSchema } from './BasicInfoForm.model';
import IssuerSubForm from './components/IssuerSubForm';
import OfferingVisibilitySubForm from './components/OfferingVisibilitySubForm';
import SecuritySubForm from './components/SecuritySubForm';
import WallCrossSubForm from './components/WallCrossSubForm';
import { useCheckInternalOfferingsTrait } from './hooks/useCheckInternalOfferingsTrait';
import { useCheckPrivatePlacementTrait } from './hooks/useCheckPrivatePlacementTrait';
import { useCheckWallCrossTrait } from './hooks/useCheckWallCrossTrait';

export type Props = {
  offering: OfferingSetup_BasicInfoRoute_OfferingFullPartsFragment | null;
  publishedOffering: OfferingSetup_BasicInfoRoute_PublishedOfferingFullPartsFragment | null;
  panelHeaderContent?: ReactElement<ButtonProps>;
  submitting?: boolean;
  disableUnsavedChangesGuard?: boolean;
  onSubmit: (payload: FormType) => void;
  countryCode: string;
  initialPricingCurrencyCode: string;
};

/**
 * Basic Info Form (Offering Type, Issuer, Security)
 * Creates an Offering Draft, or updates info for an Offering
 */
const BasicInfoForm: React.FC<Props> = ({
  offering,
  panelHeaderContent,
  submitting,
  onSubmit,
  disableUnsavedChangesGuard = false,
  initialPricingCurrencyCode,
  countryCode,
}) => {
  const hasInternalOfferingsTrait = useCheckInternalOfferingsTrait();
  const hasWallCrossTrait = useCheckWallCrossTrait();
  const hasPrivatePlacementTrait = useCheckPrivatePlacementTrait();

  const formik = useFormik<FormType>({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    validationSchema: getValidationSchema(hasInternalOfferingsTrait),
    initialValues: {
      isInternal: defaultTo(offering?.isInternal, null),
      type: defaultTo(
        offering?.type,
        hasPrivatePlacementTrait ? OfferingType.PrivatePlacement : OfferingType.Ipo
      ),
      issuer: {
        name: defaultTo(offering?.issuer.name, ''),
        businessDescription: defaultTo(offering?.issuer.businessDescription, ''),
        sector: defaultTo(offering?.issuer.sector, null),
        subSector: defaultTo(offering?.issuer.subSector, ''),
        address: defaultTo(offering?.issuer.address, null),
        website: defaultTo(offering?.issuer.website, null),
        lei: defaultTo(offering?.issuer.lei, null),
      },
      security: {
        type: defaultTo(offering?.security.type, SecurityType.CommonOrdinaryShare),
        shareClass: defaultTo(offering?.security.shareClass, null),
        securityDescription: defaultTo(offering?.security.securityDescription, ''),
      },
      pricingCurrencyCode: defaultTo(offering?.pricingCurrencyCode, initialPricingCurrencyCode),
      isWallCrossEnabled: defaultTo(offering?.isWallCrossEnabled, false),
    },
    onSubmit: values => {
      const isConvertible = values.type === OfferingType.Convertible;
      const isPrivatePlacement = values.type === OfferingType.PrivatePlacement;

      const pricingCurrencyCode =
        'pricingCurrencyCode' in values ? values.pricingCurrencyCode : 'USD';

      const security: FormType['security'] =
        isConvertible || isPrivatePlacement
          ? {
              type: values.security.type,
              securityDescription: values.security.securityDescription || null,
            }
          : {
              type: values.security.type,
              shareClass: defaultTo(values.security.shareClass, null),
            };

      return onSubmit({
        isInternal: values.isInternal ?? false,
        type: values.type ?? null,
        security,
        issuer: {
          name: values.issuer.name,
          lei: values.issuer.lei || null,
          website: values.issuer.website || null,
          address: values.issuer.address || null,
          businessDescription: values.issuer.businessDescription || null,
          sector: values.issuer.sector ?? null,
          subSector: values.issuer.subSector || null,
        },
        pricingCurrencyCode,
        isWallCrossEnabled: values.isWallCrossEnabled,
      });
    },
  });

  const isInternalOffering = formik.values.isInternal;
  const sectionActions = panelHeaderContent ? [panelHeaderContent] : [];

  return (
    <FormikProvider value={formik}>
      <FormikUnsavedChangesGuard when={!disableUnsavedChangesGuard && formik.dirty}>
        <Form noValidate>
          <Grid container item spacing={2} mt={1} direction="column">
            <Section title="Issuer Details" actions={sectionActions}>
              <IssuerSubForm
                offering={offering}
                submitting={submitting}
                isInternalOffering={isInternalOffering}
                countryCode={countryCode}
              />
            </Section>
            <SecuritySubForm submitting={submitting} />
            {hasInternalOfferingsTrait && (
              <OfferingVisibilitySubForm disabled={submitting || !!offering?.id} />
            )}
            {hasWallCrossTrait && <WallCrossSubForm submitting={submitting} />}
          </Grid>
        </Form>
      </FormikUnsavedChangesGuard>
    </FormikProvider>
  );
};

export default BasicInfoForm;
